import {
  BETTING__OBB_CHANGE_STAKE_ACTION,
  BETTING__OBB_UPDATE_QUOTES,
  BettingObbChangeStakeAction,
  BettingObbUpdateQuotesAction,
  BETTING__OBB_VALIDATE_STAKE,
  BettingObbValidateStake,
  BETTING__OBB_TRANSFER_POTENTIAL_BET_STAKE,
} from "@ppb/tbd-store/actions/betting";
import { BetslipApplicationState } from "../../store/state/BetslipApplicationState";
import type { BetControlsProps } from "../BetControls/snowflakes/BetControls/BetControls.types";
import { MapDispatchToProps, MapStateToPropsFactory } from "react-redux";
import { Dispatch } from "redux";
import {
  createGetObbLegsUrnsByPotentialBetIdSelector,
  createGetObbPotentialBetsByIdSelector,
  getObbBettingState,
} from "@ppb/tbd-store/state/betting/obb-betting/obb-betting-selectors";
import { createOddsMovementSelector } from "../../store/state/betslip-module/betslip-odds-movement-selectors";
import {
  getBetslipPlaceStatus,
  getObbOddsMovement,
  getIsBetslipLoaded,
} from "../../store/state/betslip-module/betslip-module-selectors";
import { isStakeValid } from "../../helpers/obb-territory-rules";
import { i18n } from "../../helpers/i18n";
import { buildObbPotentialBetOdds } from "../betslip-formatters";
import { currencyFormatWithDecimalPlaces } from "../../formatters/currency-adapter";
import { buildObbFailureDetails } from "./obb-bet-controls-mapper";
import type {
  BetslipLocalization,
  SportsbookBetslipCapabilities,
  SportsbookBetslipConfig,
  SportsbookBetslipPreferences,
} from "../../contract";
import { toKeyboardSeparator } from "../../helpers/keyboard-helper";

type ObbFullBetI18n = {
  odds: string;
  stake: string;
  returns: string;
  oddsMovementUp: string;
  oddsMovementDown: string;
};

export type ContainerProps = {
  potentialBetId: string;
  shouldFocusStakeField?: boolean;
  hasAvailabilityHints?: boolean;
  hasReturnsLabel?: boolean;
  localization: BetslipLocalization;
  config: SportsbookBetslipConfig;
  capabilities: SportsbookBetslipCapabilities;
  preferences: SportsbookBetslipPreferences;
};

type CardProps = {
  i18n: ObbFullBetI18n;
  isSingleObbBetBetslip: boolean;
  hasStakeCaret: boolean;
} & Pick<
  BetControlsProps,
  | "separator"
  | "currencySymbol"
  | "odds"
  | "oddsMovement"
  | "formattedPotentialReturns"
  | "stake"
  | "isPanelDisabled"
  | "isStakeValid"
  | "hintType"
  | "hintMessage"
  | "displayReturns"
>;

export type StateProps = CardProps | Record<string, never>;

export const makeMapStateToProps: MapStateToPropsFactory<StateProps, ContainerProps, BetslipApplicationState> = () => {
  const i18nLabels: ObbFullBetI18n = {
    odds: i18n({ key: "I18N.BETSLIP.ODDS" }),
    stake: i18n({ key: "I18N.BETSLIP.STAKE" }),
    returns: "",
    oddsMovementUp: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_INCREASE" }),
    oddsMovementDown: i18n({ key: "I18N.BETSLIP.ODDS_MOVEMENT_DECREASE" }),
  };

  const getObbPotentialBet = createGetObbPotentialBetsByIdSelector();
  const getOddsMovement = createOddsMovementSelector();

  return (
    state: BetslipApplicationState,
    { potentialBetId, hasAvailabilityHints, hasReturnsLabel, localization, capabilities, preferences },
  ): StateProps => {
    const { currencySymbol } = localization;
    const obbLegsByPotentialBet = createGetObbLegsUrnsByPotentialBetIdSelector();
    const obbLegs = obbLegsByPotentialBet(state, potentialBetId);
    const { quote, stake, potentialReturns } = getObbPotentialBet(state, potentialBetId);
    const { validations, failures } = getObbBettingState(state);
    if (!getIsBetslipLoaded(state)) {
      return {};
    }

    const obbOddsMovement = getObbOddsMovement(state);
    const isSingleObbBetBetslip = Object.keys(obbOddsMovement).length === 1;
    const oddsMovementMap = getOddsMovement(obbOddsMovement);
    const odds = buildObbPotentialBetOdds(quote, preferences.oddsDisplay);

    const isPlacing = getBetslipPlaceStatus(state) === "INPROGRESS";
    const obbFailureDetails = buildObbFailureDetails(failures.legs, obbLegs);
    const isPanelDisabled = isPlacing || obbFailureDetails.hasFailure;
    const hasHints = hasAvailabilityHints && obbFailureDetails.hint;

    let formattedPotentialReturns: string | undefined;

    if (hasReturnsLabel) {
      const showReturnsWithValue = potentialReturns != null || !!quote;

      i18nLabels.returns = showReturnsWithValue
        ? i18n({ key: "I18N.BETSLIP.RETURNS" })
        : i18n({ key: "I18N.BETSLIP.BET_RETURNS_TBD" });

      formattedPotentialReturns = showReturnsWithValue
        ? currencyFormatWithDecimalPlaces({
            ...localization,
            value: potentialReturns ?? 0,
            decimalPlaces: 2,
          })
        : undefined;
    }

    const potentialBetValidations = validations.potentialBets[potentialBetId];
    const isStakeInputCaretEnabled = capabilities.stakeInputCaret.enabled;

    return {
      separator: toKeyboardSeparator(localization.decimalSeparator),
      currencySymbol,
      isPanelDisabled,
      i18n: i18nLabels,
      odds,
      stake: stake ?? undefined,
      oddsMovement: oddsMovementMap[potentialBetId]?.movement,
      formattedPotentialReturns,
      isStakeValid: isStakeValid(localization, potentialBetValidations),
      hintType: hasHints ? obbFailureDetails.hint?.hintType : undefined,
      hintMessage: hasHints ? obbFailureDetails.hint?.hintMessage : undefined,
      displayReturns: !!hasReturnsLabel,
      isSingleObbBetBetslip,
      hasStakeCaret: isStakeInputCaretEnabled,
    };
  };
};

export type DispatchProps = {
  dispatchStakeChange: ({ potentialBetId, newValue }: { potentialBetId: string; newValue?: number }) => void;
  dispatchStakeValidate: ({ potentialBetId }: { potentialBetId: string }) => void;
  dispatchQuotesUpdate: () => void;
  dispatchTransferStake: ({
    oldPotentialBetId,
    newPotentialBetId,
  }: {
    oldPotentialBetId: string;
    newPotentialBetId: string;
  }) => void;
};

export const mapDispatchToProps: MapDispatchToProps<DispatchProps, ContainerProps> = (dispatch: Dispatch) => ({
  dispatchStakeValidate: ({ potentialBetId }: { potentialBetId: string }) => {
    dispatch<BettingObbValidateStake>({
      type: BETTING__OBB_VALIDATE_STAKE,
      payload: { potentialBetId },
    });
  },
  dispatchStakeChange: ({ potentialBetId, newValue }: { potentialBetId: string; newValue?: number }) => {
    dispatch<BettingObbChangeStakeAction>({
      type: BETTING__OBB_CHANGE_STAKE_ACTION,
      payload: { potentialBetId, newValue: newValue || null },
    });
  },
  dispatchQuotesUpdate: () => {
    dispatch<BettingObbUpdateQuotesAction>({
      type: BETTING__OBB_UPDATE_QUOTES,
    });
  },
  dispatchTransferStake: ({
    oldPotentialBetId,
    newPotentialBetId,
  }: {
    oldPotentialBetId: string;
    newPotentialBetId: string;
  }) => {
    dispatch({
      type: BETTING__OBB_TRANSFER_POTENTIAL_BET_STAKE,
      payload: { oldPotentialBetId, newPotentialBetId },
    });
  },
});
